// ─────────────────────────────────────────────────────────────
//  supabaseDB.js  —  Faida cloud data sync via Supabase REST
//
//  Uses a single table  user_data(user_id, collection, data)
//  with one JSONB row per user per collection.
//  All writes go to localStorage first, then sync in background.
// ─────────────────────────────────────────────────────────────
import { mergeById, detectConflicts } from './syncMerge';
import { config } from './platform/config';

// ── Conflict log (dev visibility) ─────────────────────────────────────────
// A capped in-memory ring of same-record concurrent-edit conflicts detected at
// sync time — enough to debug AFTER the losing edit is gone, incl. a snapshot
// of the losing record. Read via getConflictLog().
const CONFLICT_LOG = [];
const CONFLICT_LIMIT = 100;
export const getConflictLog = () => CONFLICT_LOG.slice();
export const clearConflictLog = () => { CONFLICT_LOG.length = 0; };
const recordConflicts = (name, cloud, local) => {
  try {
    const conflicts = detectConflicts(cloud, local);
    if (!conflicts.length) return;
    const at = new Date().toISOString();
    const cloudById = new Map((cloud || []).filter(r => r && r.id != null).map(r => [String(r.id), r]));
    const localById = new Map((local || []).filter(r => r && r.id != null).map(r => [String(r.id), r]));
    conflicts.forEach((c) => {
      const loser = c.winner === 'local' ? cloudById.get(String(c.id)) : localById.get(String(c.id));
      CONFLICT_LOG.push({
        collection: name, recordId: c.id,
        localUpdatedAt: (localById.get(String(c.id)) || {}).updatedAt || null,
        cloudUpdatedAt: (cloudById.get(String(c.id)) || {}).updatedAt || null,
        winner: c.winner, reason: c.reason, detectedAt: at,
        losingSnapshot: loser ? JSON.parse(JSON.stringify(loser)) : null,
      });
    });
    while (CONFLICT_LOG.length > CONFLICT_LIMIT) CONFLICT_LOG.shift();
    try { window.dispatchEvent(new CustomEvent('faida:conflict', { detail: { collection: name, count: conflicts.length } })); } catch (e) {}
    console.warn(`[FaidaDB] ${conflicts.length} same-record conflict(s) in ${name} (newest-wins):`, conflicts);
  } catch (e) { /* never let conflict logging break sync */ }
};

// Configuration from environment only (Amendment A12 — no hardcoded fallbacks).
const SUPABASE_URL = config.SUPABASE_URL;
const SUPABASE_ANON_KEY = config.SUPABASE_ANON_KEY;

const DB_URL = `${SUPABASE_URL}/rest/v1/user_data`;
const STORAGE_URL = `${SUPABASE_URL}/storage/v1/object/authenticated`;

const headers = (token) => ({
  'Content-Type': 'application/json',
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${token}`,
  Prefer: 'return=minimal',
});

// ── Upload image to Supabase Storage ──────────────────────────────
export const uploadBusinessLogo = async (userId, token, base64Image) => {
  if (!userId || !token || !base64Image) return null;
  try {
    // Convert base64 data URL to blob
    let blob;
    if (base64Image.startsWith('data:')) {
      // It's a data URL, extract the base64 part
      const parts = base64Image.split(',');
      const base64 = parts[1];
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      blob = new Blob([bytes], { type: 'image/jpeg' });
    } else {
      // Fallback: try to fetch as-is
      const response = await fetch(base64Image);
      blob = await response.blob();
    }

    // Create filename
    const filename = `business-logos/${userId}/logo.jpg`;

    // Upload to Supabase Storage
    const uploadRes = await fetch(
      `${STORAGE_URL}/business-logos/${filename}`,
      {
        method: 'POST',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: blob,
      }
    );

    if (!uploadRes.ok) {
      if (uploadRes.status === 401) {
        fireSessionExpired();
        return null;
      }
      if (uploadRes.status === 404) {
        console.warn('[FaidaDB] Supabase storage bucket not found. Make sure "business-logos" bucket exists and is public.');
        return null;
      }
      const err = await uploadRes.text();
      console.warn('[FaidaDB] logo upload failed:', uploadRes.status, err);
      return null;
    }

    // Return public URL
    const logoUrl = `${SUPABASE_URL}/storage/v1/object/public/business-logos/${filename}`;
    return logoUrl;
  } catch (e) {
    console.warn('[FaidaDB] logo upload error:', e);
    return null;
  }
};

// ── Delete business logo from storage ─────────────────────────────
export const deleteBusinessLogo = async (userId, token) => {
  if (!userId || !token) return false;
  try {
    const filename = `business-logos/${userId}/logo.jpg`;
    const res = await fetch(
      `${STORAGE_URL}/business-logos/${filename}`,
      {
        method: 'DELETE',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok && res.status !== 404) {
      console.warn('[FaidaDB] logo delete failed:', res.status);
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[FaidaDB] logo delete error:', e);
    return false;
  }
};

// ── SQL to run once in Supabase SQL Editor ─────────────────────────
// (kept here as documentation)
//
// CREATE TABLE IF NOT EXISTS public.user_data (
//   id          uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   user_id     uuid NOT NULL,
//   collection  text NOT NULL,
//   data        jsonb NOT NULL DEFAULT '[]'::jsonb,
//   updated_at  timestamptz DEFAULT now(),
//   UNIQUE (user_id, collection)
// );
// ALTER TABLE public.user_data ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "own_data" ON public.user_data
//   FOR ALL USING (auth.uid() = user_id)
//   WITH CHECK (auth.uid() = user_id);

// ── Upsert one collection to Supabase ─────────────────────────────
// ── Session-expired event (fires when any request gets 401) ───────────
const fireSessionExpired = () => {
  window.dispatchEvent(new CustomEvent('faida:session-expired'));
};

// Fetch a single collection's array (for pull-before-push).
const fetchCollection = async (userId, token, name) => {
  const res = await fetch(`${DB_URL}?user_id=eq.${userId}&collection=eq.${encodeURIComponent(name)}&select=data,updated_at`, { headers: headers(token) });
  if (!res.ok) { if (res.status === 401) fireSessionExpired(); throw new Error(`fetch ${name} ${res.status}`); }
  const rows = await res.json();
  const row = (rows || [])[0];
  return { data: row && Array.isArray(row.data) ? row.data : null, updatedAt: row && row.updated_at };
};

// syncCollection — PULL-BEFORE-PUSH record-level convergence.
//
// The store is one JSONB array per (user_id, collection). A naive whole-array
// push is last-write-wins and can drop another device's independent edits. So
// before writing we FETCH the current cloud array and mergeById(cloud, local)
// record-by-record (newest updatedAt/deletedAt wins; deletion wins ties), then
// push the MERGE. Independent-record edits from both devices therefore survive.
// Optimistic-concurrency guard: if the cloud row changes between our fetch and
// our write, we refetch+remerge and retry a bounded number of times.
//
// Returns the merged array so callers may converge local state; merge is
// idempotent so a repeated sync is a no-op.
export const syncCollection = async (userId, token, name, data, opts = {}) => {
  if (!userId || !token) return data;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('faida-pause-sync') === '1') {
      console.log(`⏸️ [FaidaDB] sync paused — ${name} kept local only`);
      return data;
    }
  } catch (e) { /* ignore */ }

  const isArray = Array.isArray(data);
  // Retry the whole pull-merge-push a couple of times so that if another device
  // writes between our fetch and our push, the NEXT attempt pulls their write
  // and re-merges it in (best-effort optimistic concurrency for the JSONB-row
  // model — see the limitation note below).
  const maxRetries = 2;
  let merged = data;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // 1. PULL the current cloud copy and merge record-by-record (arrays only;
      //    settings/scalars are pushed as-is).
      if (isArray) {
        try {
          const cloud = await fetchCollection(userId, token, name);
          if (Array.isArray(cloud.data)) {
            recordConflicts(name, cloud.data, merged); // log same-record conflicts before they resolve
            merged = mergeById(cloud.data, merged);
          }
        } catch (e) { /* offline / first write — push what we have */ }
      }
      // 2. PUSH the merge (not the bare local array).
      const res = await fetch(`${DB_URL}?on_conflict=user_id,collection`, {
        method: 'POST',
        headers: { ...headers(token), Prefer: 'resolution=merge-duplicates,return=minimal' },
        body: JSON.stringify({ user_id: userId, collection: name, data: merged, updated_at: new Date().toISOString() }),
      });
      if (res.status === 401) { fireSessionExpired(); return merged; }
      if (!res.ok) {
        // Retry (re-pull + re-merge) a bounded number of times, else give up.
        if (attempt < maxRetries) continue;
        console.warn(`[FaidaDB] sync failed for ${name}:`, await res.text());
        return merged;
      }
      console.log(`✅ [FaidaDB] synced ${name} (${isArray ? merged.length + ' records, pull-before-push' : 'scalar'})`);
      if (typeof opts.onMerged === 'function' && isArray) opts.onMerged(merged);
      return merged;
    } catch (e) {
      console.warn(`[FaidaDB] offline, ${name} queued locally`);
      return merged;
    }
  }
  return merged;
};

// ── Fetch all collections for a user ──────────────────────────────
export const fetchAllData = async (userId, token) => {
  if (!userId || !token) return null;
  try {
    const res = await fetch(
      `${DB_URL}?user_id=eq.${userId}&select=collection,data`,
      { headers: headers(token) }
    );
    if (!res.ok) {
      if (res.status === 401) { fireSessionExpired(); return null; }
      console.warn('[FaidaDB] fetch failed:', res.status);
      return null;
    }
    const rows = await res.json();
    // Convert array of {collection, data} into a plain object keyed by collection
    const result = {};
    (rows || []).forEach((row) => {
      result[row.collection] = row.data;
    });
    return result; // e.g. { sales: [...], products: [...], settings: {...} }
  } catch (e) {
    console.warn('[FaidaDB] fetch error (offline?):', e);
    return null;
  }
};

// ── Delete all data for a user (account deletion helper) ──────────
export const deleteAllUserData = async (userId, token) => {
  if (!userId || !token) return;
  try {
    await fetch(`${DB_URL}?user_id=eq.${userId}`, {
      method: 'DELETE',
      headers: headers(token),
    });
  } catch (e) {
    console.warn('[FaidaDB] delete error:', e);
  }
};
